import * as Tooltip from '@radix-ui/react-tooltip'
import { useCallback, useEffect, useState } from 'react'
import { FiPlus, FiX } from 'react-icons/fi'
import { IoPlayOutline } from 'react-icons/io5'
import { PiPlayBold } from 'react-icons/pi'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { addSkill, listCategories, listSkills, removeSkill, SkillCategoryItem, SkillItem } from '@/api/skill'
import ChatHeader from '@/components/ChatHeader'
import useDeviceType from '@/hooks/useDeviceType'
import { useChatStore, useUserStore } from '@/store'

const formatUsage = (count: number) => {
  if (!count) return '0人聊过'
  if (count >= 10000) {
    const formatted = (count / 10000).toFixed(1).replace(/\.0$/, '')
    return `${formatted}万人聊过`
  }
  return `${count}人聊过`
}

const isImageUrl = (value?: string) => !!value && /^(https?:\/\/|\/|data:image\/)/i.test(value)

export default function ChatSkills() {
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)
  const setIsLoginModalOpen = useChatStore((state) => state.setIsLoginModalOpen)
  const deviceType = useDeviceType()
  const isMobileOrPad = deviceType === 'mobile' || deviceType === 'tablet'

  const [categories, setCategories] = useState<SkillCategoryItem[]>([])
  const [activeTabId, setActiveTabId] = useState<number>(0)
  const [skills, setSkills] = useState<SkillItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [actionSkillId, setActionSkillId] = useState<number | null>(null)
  const [pendingSkill, setPendingSkill] = useState<SkillItem | null>(null)
  const pageSize = 20

  const loadSkills = useCallback(
    async (pageNum: number, isLoadMore = false) => {
      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }

      try {
        const categoryParam = activeTabId === 0 ? '' : activeTabId
        const res = await listSkills(categoryParam, pageNum, pageSize)
        const newSkills = res.data || []

        if (isLoadMore) {
          setSkills((prev) => [...prev, ...newSkills])
        } else {
          setSkills(newSkills)
        }

        setHasMore(newSkills.length >= pageSize)
        setPage(pageNum)
      } catch (error) {
        console.error('Failed to load skills:', error)
        toast.error('技能列表加载失败')
        if (!isLoadMore) setSkills([])
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [activeTabId]
  )

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await listCategories()
        const nextCategories = res.data.slice().sort((a, b) => a.sort - b.sort)
        setCategories([{ id: 0, name: '全部', icon: '', sort: -1 }, ...nextCategories])
        setActiveTabId(0)
      } catch (error) {
        console.error('Failed to load categories:', error)
      }
    }

    void fetchCategories()
  }, [])

  useEffect(() => {
    void loadSkills(1)
  }, [loadSkills])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!isMobileOrPad || !hasMore || isLoading || isLoadingMore) return

      const target = e.currentTarget
      const scrollHeight = target.scrollHeight
      const scrollTop = target.scrollTop
      const clientHeight = target.clientHeight

      if (scrollHeight - scrollTop - clientHeight < 100) {
        void loadSkills(page + 1, true)
      }
    },
    [isMobileOrPad, hasMore, isLoading, isLoadingMore, loadSkills, page]
  )

  const handleToggleSkill = async (skill: SkillItem) => {
    if (!user) {
      setIsLoginModalOpen(true)
      return
    }

    setPendingSkill(skill)
  }

  const handleConfirmToggleSkill = async () => {
    if (!pendingSkill) return

    const targetSkill = pendingSkill
    const isAdded = targetSkill.added

    setActionSkillId(targetSkill.id)
    try {
      if (isAdded) {
        await removeSkill(targetSkill.id)
        toast.success('移除成功')
        setSkills((prev) => prev.map((item) => (item.id === targetSkill.id ? { ...item, added: false } : item)))
        setPendingSkill(null)
        await loadSkills(1)
        return
      }

      await addSkill(targetSkill.id)
      toast.success('添加成功')
      setSkills((prev) => prev.map((item) => (item.id === targetSkill.id ? { ...item, added: true } : item)))
      setPendingSkill((prev) => (prev && prev.id === targetSkill.id ? { ...prev, added: true } : prev))
    } catch (error) {
      console.error(isAdded ? 'Failed to remove skill:' : 'Failed to add skill:', error)
      toast.error(isAdded ? '移除失败' : '添加失败')
    } finally {
      setActionSkillId(null)
    }
  }

  const handleRunSkill = () => {
    if (!pendingSkill) return
    navigate(`/chat?skillId=${pendingSkill.id}`)
  }

  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="flex flex-1 flex-col overflow-hidden bg-white transition-all duration-300">
        <ChatHeader showTitle={false} showResourcePreview={false} />

        <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
          <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-8">
            {/* Top Ad Banner */}
            {/* <div className="mb-4 flex h-48 w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400 md:h-64">广告运营位</div> */}

            {/* Middle Ad Banners */}
            {/* <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="flex h-32 items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400">广告运营位</div>
            <div className="flex h-32 items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400">广告运营位</div>
            <div className="flex h-32 items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400">广告运营位</div>
          </div> */}

            {/* Tabs */}
            <div className="mb-6 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTabId(tab.id)}
                  className={`flex h-[36px] px-[14px] items-center justify-center whitespace-nowrap rounded-[10px] text-sm font-medium transition-colors ${
                    activeTabId === tab.id ? 'bg-[#1769F6] border border-[#1769F6] text-white' : 'bg-white border border-[#E4E4E4] text-[rgba(0,0,0,0.87)] hover:bg-gray-50'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            {/* Skills Grid */}
            <div className="grid grid-cols-1 gap-4 desktop:grid-cols-2">
              {isLoading ? (
                <div className="col-span-full rounded-2xl border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-400">加载中...</div>
              ) : skills.length === 0 ? (
                <div className="col-span-full rounded-2xl border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-400">当前分类暂无技能</div>
              ) : (
                skills.map((skill) => {
                  const skillName = skill.name
                  const isAdded = skill.added
                  const isOperating = actionSkillId === skill.id

                  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    if (isAdded) {
                      navigate(`/chat?skillId=${skill.id}`)
                      return
                    }
                    void handleToggleSkill(skill)
                  }

                  return (
                    <div
                      key={skill.id}
                      onClick={() => void handleToggleSkill(skill)}
                      className="group flex items-center gap-4 rounded-2xl border border-gray-100 p-4 transition-all hover:border-blue-100 hover:shadow-sm cursor-pointer"
                    >
                      <div className="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-lg font-medium text-gray-600">
                        {isImageUrl(skill.avatar) ? <img src={skill.avatar} alt={skillName} className="h-full w-full object-cover" /> : <span>{skill.avatar || skillName.slice(0, 1)}</span>}
                      </div>
                      <div className="flex flex-1 flex-col justify-center overflow-hidden">
                        <h3 className="mb-1 truncate text-base font-medium text-gray-900">{skillName}</h3>
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <p className="mb-2 line-clamp-1 text-sm text-gray-500">{skill.description}</p>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              className="z-[150] max-w-[300px] rounded-md bg-gray-900 px-3 py-2 text-sm text-white shadow-md animate-in fade-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95"
                              sideOffset={5}
                            >
                              {skill.description}
                              <Tooltip.Arrow className="fill-gray-900" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <span className="flex h-3 w-3 items-center justify-center rounded-full bg-gray-200 text-[8px] text-white">🔥</span>
                          {formatUsage(skill.useCount)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleActionClick}
                        disabled={isOperating}
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all md:opacity-100 'text-gray-400 opacity-0 group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:opacity-100' ${isOperating ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        {isAdded ? <PiPlayBold className="h-5 w-5" /> : <FiPlus className="h-5 w-5" />}
                      </button>
                    </div>
                  )
                })
              )}
            </div>

            {/* Load More */}
            {!isLoading && skills.length > 0 && hasMore && (
              <div className="mt-6 flex justify-center pb-6">
                {!isMobileOrPad ? (
                  <button
                    onClick={() => void loadSkills(page + 1, true)}
                    disabled={isLoadingMore}
                    className="rounded-full border border-gray-200 bg-white px-6 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isLoadingMore ? '加载中...' : '加载更多'}
                  </button>
                ) : (
                  isLoadingMore && <div className="text-sm text-gray-400">加载中...</div>
                )}
              </div>
            )}

            {!hasMore && skills.length > 0 && <div className="mt-6 pb-6 text-center text-sm text-gray-400">没有更多技能了</div>}
          </div>
        </div>

        {pendingSkill && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm transition-opacity duration-300" onClick={() => setPendingSkill(null)}>
            <div
              className="relative w-full max-w-[420px] overflow-hidden rounded-3xl bg-white shadow-2xl transition-all duration-300 animate-in fade-in zoom-in-95"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={() => setPendingSkill(null)} className="absolute right-4 top-4 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                <FiX className="h-5 w-5" />
              </button>

              <div className={`h-28 bg-gradient-to-br ${pendingSkill.added ? 'from-red-50 via-orange-50 to-white' : 'from-blue-50 via-indigo-50 to-white'}`} />

              <div className="relative px-6 pb-6">
                <div className="-mt-10 mb-4 flex items-start gap-4">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white bg-white text-2xl font-semibold text-gray-700 shadow-lg">
                    {isImageUrl(pendingSkill.avatar) ? (
                      <img src={pendingSkill.avatar} alt={pendingSkill.name} className="h-full w-full object-cover" />
                    ) : (
                      <span>{pendingSkill.avatar || pendingSkill.name.slice(0, 1)}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 pt-12">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${pendingSkill.added ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-600'}`}>
                        {pendingSkill.added ? '已添加技能' : '未添加技能'}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">{pendingSkill.categoryName}</span>
                    </div>
                    <h3 className="truncate text-xl font-semibold text-gray-900">{pendingSkill.name}</h3>
                  </div>
                </div>

                <p className="mb-5 line-clamp-3 text-sm leading-6 text-gray-600">{pendingSkill.description}</p>

                <div className="mb-6 flex items-center gap-3 rounded-2xl bg-gray-50 px-4 py-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-base shadow-sm">🔥</div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{formatUsage(pendingSkill.useCount)}</div>
                    <div className="text-xs text-gray-500">添加后点击运行跳转到新对话</div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                  {pendingSkill.added ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void handleConfirmToggleSkill()}
                        disabled={actionSkillId === pendingSkill.id}
                        className={`rounded-xl bg-red-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-600 ${
                          actionSkillId === pendingSkill.id ? 'cursor-not-allowed opacity-60' : ''
                        }`}
                      >
                        {actionSkillId === pendingSkill.id ? '处理中...' : '移除'}
                      </button>
                      <button
                        type="button"
                        onClick={handleRunSkill}
                        disabled={actionSkillId === pendingSkill.id}
                        className={`inline-flex items-center gap-1 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 ${
                          actionSkillId === pendingSkill.id ? 'cursor-not-allowed opacity-60' : ''
                        }`}
                      >
                        <IoPlayOutline className="h-4 w-4" />
                        运行
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleConfirmToggleSkill()}
                      disabled={actionSkillId === pendingSkill.id}
                      className={`rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 ${
                        actionSkillId === pendingSkill.id ? 'cursor-not-allowed opacity-60' : ''
                      }`}
                    >
                      {actionSkillId === pendingSkill.id ? '处理中...' : '添加'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Tooltip.Provider>
  )
}
